import { useQuery } from '@tanstack/react-query'
import { getMemberRewards } from '@vista-digital/occ-domain'
import { useI18n } from '@vista-digital/occ-ui/src'
import { useMemo } from 'react'
import type { RewardType } from '@/common/model_builder/dropdowns/rewardType'
import { api } from '@/content/services/api'
import { cmsDataService } from '@/content/services/cms.data.service'
import type { RewardPropsApi } from '../models/reward.models'

export const useRewards = (config: { hideRewardTypes: RewardType[] }) => {
    const i18n = useI18n()
    const culture = cmsDataService.get()?.culture

    const { data: rewards, isLoading } = useQuery({
        queryKey: ['rewards-' + culture],
        queryFn: () => api.GET<RewardPropsApi[]>(api.RewardsList(culture)),
        staleTime: 1000 * 60 * 5,
        enabled: !!culture,
    })

    const memberRewards = getMemberRewards()

    const filteredRewards = useMemo(
        () =>
            rewards
                ?.map(reward => {
                    if (config.hideRewardTypes.includes(reward.rewardType)) {
                        return null
                    }

                    if (reward.rewardType === 'Instant Reward') {
                        const memberReward = memberRewards?.find(mr => mr.rewardId === reward.loyaltyRecognitionId)

                        if (memberReward && reward.subtitle) {
                            reward.subtitle = i18n.template(reward.subtitle, { expiryDate: memberReward.expiresAt })
                        }

                        // Filter out instant rewards if the member doesn't have the matching reward in their ocapi data
                        if (!memberReward) {
                            return null
                        }
                    }

                    return reward
                })
                .filter(Boolean) as typeof rewards,
        [config?.hideRewardTypes, i18n, memberRewards, rewards]
    )

    return { rewards: filteredRewards, isLoading }
}
